import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, MagnifyingGlass, Pencil, Trash, Eye, X, UploadSimple } from '@phosphor-icons/react';
import { getItems, getCategories, createItem, updateItem, deleteItem } from '../../utils/api';
import { formatPrice, formatDate } from '../../utils/helpers';
import { toast } from 'sonner';
import BulkImportModal from '../../components/admin/BulkImportModal';

const AdminItems = () => {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    purchase_rate: '',
    selling_rate: '',
    mrp: '',
    quantity: '',
    category_id: '',
    type: '',
    design: '',
    material: '',
    closure_type: '',
    styling_tip: '',
    highlights: '',
  });
  const [images, setImages] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);

  const fetchCategories = async () => {
    try {
      const res = await getCategories();
      setCategories(res.data.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await getItems({ 
        page: pagination.page, 
        limit: 10, 
        search: search || undefined,
        include_deleted: true 
      });
      setItems(res.data.data || []);
      setPagination(prev => ({
        ...prev,
        totalPages: res.data.pagination?.total_pages || 1,
        total: res.data.pagination?.total || 0
      }));
    } catch (error) {
      console.error('Error fetching items:', error);
      toast.error('Failed to fetch items');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchItems();
  }, [pagination.page, search]);

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      purchase_rate: '',
      selling_rate: '',
      mrp: '',
      quantity: '',
      category_id: '',
      type: '',
      design: '',
      material: '',
      closure_type: '',
      styling_tip: '',
      highlights: '',
    });
    setImages([]);
    setEditingItem(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowEditModal(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name || '',
      description: item.description || '',
      purchase_rate: item.purchase_rate || '',
      selling_rate: item.selling_rate || '',
      mrp: item.mrp || '',
      quantity: (item.quantity ?? item.stock_quantity) || '',
      category_id: item.category_id || '',
      type: item.type || '',
      design: item.design || '',
      material: item.material || '',
      closure_type: item.closure_type || '',
      styling_tip: item.styling_tip || '',
      highlights: item.highlights || '',
    });
    setShowEditModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingItem) {
        // Update existing item
        const updateData = {
          name: formData.name,
          description: formData.description || null,
          purchase_rate: parseFloat(formData.purchase_rate),
          selling_rate: parseFloat(formData.selling_rate),
          mrp: formData.mrp ? parseFloat(formData.mrp) : null,
          quantity: parseInt(formData.quantity),
          category_id: formData.category_id ? parseInt(formData.category_id) : null,
          type: formData.type || null,
          design: formData.design || null,
          material: formData.material || null,
          closure_type: formData.closure_type || null,
          styling_tip: formData.styling_tip || null,
          highlights: formData.highlights || null,
        };
        await updateItem(editingItem.id, updateData);
        toast.success('Item updated successfully');
      } else {
        // Create new item
        const formDataToSend = new FormData();
        formDataToSend.append('name', formData.name);
        formDataToSend.append('description', formData.description || '');
        formDataToSend.append('purchase_rate', formData.purchase_rate);
        formDataToSend.append('selling_rate', formData.selling_rate);
        if (formData.mrp) {
          formDataToSend.append('mrp', formData.mrp);
        }
        formDataToSend.append('quantity', formData.quantity || '0');
        formDataToSend.append('type', formData.type || '');
        formDataToSend.append('design', formData.design || '');
        formDataToSend.append('material', formData.material || '');
        formDataToSend.append('closure_type', formData.closure_type || '');
        formDataToSend.append('styling_tip', formData.styling_tip || '');
        formDataToSend.append('highlights', formData.highlights || '');
        if (formData.category_id) {
          formDataToSend.append('category_id', formData.category_id);
        }
        images.forEach((image) => {
          formDataToSend.append('images', image);
        });
        await createItem(formDataToSend);
        toast.success('Item created successfully');
      }
      
      setShowEditModal(false);
      resetForm();
      fetchItems();
    } catch (error) {
      console.error('Error saving item:', error);
      toast.error(error.response?.data?.detail || 'Failed to save item');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      await deleteItem(id);
      toast.success('Item deleted successfully');
      fetchItems();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const handleView = (item) => {
    setSelectedItem(item);
    setShowModal(true);
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    setImages(files);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="font-display text-2xl text-secondary">Items Management</h1>
          <p className="font-body text-sm text-text-secondary">{pagination.total} total items</p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
          {/* Search */}
          <div className="relative flex-1 sm:w-64">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
            <input
              type="text"
              placeholder="Search items..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPagination(p => ({...p, page: 1})); }}
              data-testid="items-search"
              className="w-full pl-10 pr-4 py-2 border border-border-default focus:border-primary focus:outline-none font-body text-sm"
            />
          </div>
          
          <button
            onClick={openCreateModal}
            data-testid="add-item-btn"
            className="btn-primary flex items-center gap-2"
          >
            <Plus size={18} />
            Add Item
          </button>
          <button
            onClick={() => setShowBulkImport(true)}
            data-testid="bulk-import-btn"
            className="btn-secondary flex items-center gap-2"
          >
            <UploadSimple size={18} />
            Bulk Import
          </button>
        </div>
      </div>

      {/* Items Table */}
      <div className="admin-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border-default bg-background-subtle">
                <th className="text-left font-body text-xs uppercase tracking-wider text-text-secondary py-4 px-4">Product</th>
                <th className="text-left font-body text-xs uppercase tracking-wider text-text-secondary py-4 px-4">Category</th>
                <th className="text-right font-body text-xs uppercase tracking-wider text-text-secondary py-4 px-4">Price</th>
                <th className="text-right font-body text-xs uppercase tracking-wider text-text-secondary py-4 px-4">MRP</th>
                <th className="text-center font-body text-xs uppercase tracking-wider text-text-secondary py-4 px-4">Stock</th>
                <th className="text-center font-body text-xs uppercase tracking-wider text-text-secondary py-4 px-4">Status</th>
                <th className="text-right font-body text-xs uppercase tracking-wider text-text-secondary py-4 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i} className="border-b border-border-default">
                    <td colSpan={7} className="py-4 px-4">
                      <div className="h-12 bg-gray-100 animate-pulse rounded" />
                    </td>
                  </tr>
                ))
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center">
                    <p className="font-body text-text-secondary">No items found</p>
                    <button onClick={openCreateModal} className="btn-secondary mt-4">
                      Create First Item
                    </button>
                  </td>
                </tr>
              ) : (
                items.map((item) => (
                  <motion.tr
                    key={item.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="border-b border-border-default hover:bg-background-subtle transition-colors"
                    data-testid={`item-row-${item.id}`}
                  >
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-gray-100 overflow-hidden flex-shrink-0">
                          <img
                            src={item.image_urls?.[0] || 'https://via.placeholder.com/48'}
                            alt={item.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div>
                          <p className="font-body text-sm text-secondary font-medium">{item.name}</p>
                          <p className="font-body text-xs text-text-muted">ID: {item.id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span className="font-body text-sm text-text-secondary">
                        {item.category_name || '-'}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-right">
                      <span className="font-body text-sm text-secondary">
                        {formatPrice(item.selling_rate)}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-right">
                      <span className="font-body text-sm text-text-muted">
                        {item.mrp ? formatPrice(item.mrp) : '-'}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <span className={`font-body text-sm px-2 py-1 rounded ${
                        (item.quantity ?? item.stock_quantity ?? 0) === 0 
                          ? 'bg-red-100 text-red-700' 
                          : (item.quantity ?? item.stock_quantity ?? 0) < 5 
                            ? 'bg-yellow-100 text-yellow-700' 
                            : 'bg-green-100 text-green-700'
                      }`}>
                        {item.quantity ?? item.stock_quantity ?? 0}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <span className={`font-body text-xs px-2 py-1 rounded ${
                        item.is_deleted 
                          ? 'bg-gray-100 text-gray-600' 
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {item.is_deleted ? 'Deleted' : 'Active'}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleView(item)}
                          data-testid={`view-item-${item.id}`}
                          className="p-2 text-text-muted hover:text-primary transition-colors"
                          title="View"
                        >
                          <Eye size={18} />
                        </button>
                        <button
                          onClick={() => openEditModal(item)}
                          data-testid={`edit-item-${item.id}`}
                          className="p-2 text-text-muted hover:text-blue-500 transition-colors"
                          title="Edit"
                        >
                          <Pencil size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          data-testid={`delete-item-${item.id}`}
                          className="p-2 text-text-muted hover:text-red-500 transition-colors"
                          title="Delete"
                        >
                          <Trash size={18} />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex justify-center gap-2 p-4 border-t border-border-default">
            {[...Array(pagination.totalPages)].map((_, i) => (
              <button
                key={i}
                onClick={() => setPagination(p => ({ ...p, page: i + 1 }))}
                className={`w-8 h-8 font-body text-sm transition-colors ${
                  pagination.page === i + 1 
                    ? 'bg-primary text-white' 
                    : 'bg-background-subtle text-secondary hover:bg-primary/10'
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* View Modal */}
      {showModal && selectedItem && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between p-4 border-b border-border-default">
              <h3 className="font-display text-xl text-secondary">Item Details</h3>
              <button onClick={() => setShowModal(false)}>
                <X size={24} className="text-text-muted hover:text-secondary" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {selectedItem.image_urls?.[0] && (
                <img
                  src={selectedItem.image_urls[0]}
                  alt={selectedItem.name}
                  className="w-full h-48 object-cover"
                />
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="font-body text-xs text-text-muted uppercase">Name</p>
                  <p className="font-body text-sm text-secondary">{selectedItem.name}</p>
                </div>
                <div>
                  <p className="font-body text-xs text-text-muted uppercase">Category</p>
                  <p className="font-body text-sm text-secondary">{selectedItem.category_name || '-'}</p>
                </div>
                <div>
                  <p className="font-body text-xs text-text-muted uppercase">Selling Price</p>
                  <p className="font-body text-sm text-secondary">{formatPrice(selectedItem.selling_rate)}</p>
                </div>
                {selectedItem.mrp && (
                  <div>
                    <p className="font-body text-xs text-text-muted uppercase">MRP</p>
                    <p className="font-body text-sm text-secondary">{formatPrice(selectedItem.mrp)}</p>
                  </div>
                )}
                <div>
                  <p className="font-body text-xs text-text-muted uppercase">Purchase Price</p>
                  <p className="font-body text-sm text-secondary">{formatPrice(selectedItem.purchase_rate)}</p>
                </div>
                <div>
                  <p className="font-body text-xs text-text-muted uppercase">Stock</p>
                  <p className="font-body text-sm text-secondary">{selectedItem.quantity ?? selectedItem.stock_quantity ?? 0}</p>
                </div>
                <div>
                  <p className="font-body text-xs text-text-muted uppercase">Created</p>
                  <p className="font-body text-sm text-secondary">{formatDate(selectedItem.created_at)}</p>
                </div>
                <div>
                  <p className="font-body text-xs text-text-muted uppercase">Type</p>
                  <p className="font-body text-sm text-secondary">{selectedItem.type}</p>
                </div>
                <div>
                  <p className="font-body text-xs text-text-muted uppercase">Design</p>
                  <p className="font-body text-sm text-secondary">{selectedItem.design}</p>
                </div>
                <div>
                  <p className="font-body text-xs text-text-muted uppercase">Material</p>
                  <p className="font-body text-sm text-secondary">{selectedItem.material}</p>
                </div>
                <div>
                  <p className="font-body text-xs text-text-muted uppercase">Closure Type</p>
                  <p className="font-body text-sm text-secondary">{selectedItem.closure_type}</p>
                </div>
                <div>
                  <p className="font-body text-xs text-text-muted uppercase">Styling Tip</p>
                  <p className="font-body text-sm text-secondary">{selectedItem.styling_tip}</p>
                </div>
              </div>
              
              {selectedItem.description && (
                <div>
                  <p className="font-body text-xs text-text-muted uppercase mb-1">Description</p>
                  <p className="font-body text-sm text-text-secondary">{selectedItem.description}</p>
                </div>
              )}

              <div className="pt-4 border-t border-border-default">
                <button
                  onClick={() => { setShowModal(false); openEditModal(selectedItem); }}
                  className="btn-primary w-full"
                >
                  Edit Item
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between p-4 border-b border-border-default">
              <h3 className="font-display text-xl text-secondary">
                {editingItem ? 'Edit Item' : 'Add New Item'}
              </h3>
              <button onClick={() => { setShowEditModal(false); resetForm(); }}>
                <X size={24} className="text-text-muted hover:text-secondary" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block font-body text-sm text-text-secondary mb-2">
                  Item Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="item-name-input"
                  className="w-full px-4 py-3 border border-border-default focus:border-primary focus:outline-none font-body"
                  placeholder="e.g., Gold Necklace Set"
                />
              </div>

              <div>
                <label className="block font-body text-sm text-text-secondary mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  data-testid="item-description-input"
                  className="w-full px-4 py-3 border border-border-default focus:border-primary focus:outline-none font-body resize-none"
                  placeholder="Product description..."
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block font-body text-sm text-text-secondary mb-2">
                    Purchase Rate *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.purchase_rate}
                    onChange={(e) => setFormData({ ...formData, purchase_rate: e.target.value })}
                    required
                    data-testid="item-purchase-rate"
                    className="w-full px-4 py-3 border border-border-default focus:border-primary focus:outline-none font-body"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block font-body text-sm text-text-secondary mb-2">
                    Selling Rate *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.selling_rate}
                    onChange={(e) => setFormData({ ...formData, selling_rate: e.target.value })}
                    required
                    data-testid="item-selling-rate"
                    className="w-full px-4 py-3 border border-border-default focus:border-primary focus:outline-none font-body"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block font-body text-sm text-text-secondary mb-2">
                    MRP
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.mrp}
                    onChange={(e) => setFormData({ ...formData, mrp: e.target.value })}
                    data-testid="item-mrp"
                    className="w-full px-4 py-3 border border-border-default focus:border-primary focus:outline-none font-body"
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block font-body text-sm text-text-secondary mb-2">
                    Quantity
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                    data-testid="item-quantity"
                    className="w-full px-4 py-3 border border-border-default focus:border-primary focus:outline-none font-body"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block font-body text-sm text-text-secondary mb-2">
                    Category
                  </label>
                  <select
                    value={formData.category_id}
                    onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                    data-testid="item-category"
                    className="w-full px-4 py-3 border border-border-default focus:border-primary focus:outline-none font-body bg-white"
                  >
                    <option value="">Select Category</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block font-body text-sm text-text-secondary mb-2">
                    Type *
                  </label>
                  <input
                    type="text"
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    required
                    data-testid="item-type"
                    className="w-full px-4 py-3 border border-border-default focus:border-primary focus:outline-none font-body"
                    placeholder="e.g., Necklace, Earrings, Bracelet"
                  />
                </div>
                <div>
                  <label className="block font-body text-sm text-text-secondary mb-2">
                    Design *
                  </label>
                  <input
                    type="text"
                    value={formData.design}
                    onChange={(e) => setFormData({ ...formData, design: e.target.value })}
                    required
                    data-testid="item-design"
                    className="w-full px-4 py-3 border border-border-default focus:border-primary focus:outline-none font-body"
                    placeholder="e.g., Traditional, Modern, Fusion"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block font-body text-sm text-text-secondary mb-2">
                    Material *
                  </label>
                  <input
                    type="text"
                    value={formData.material}
                    onChange={(e) => setFormData({ ...formData, material: e.target.value })}
                    required
                    data-testid="item-material"
                    className="w-full px-4 py-3 border border-border-default focus:border-primary focus:outline-none font-body"
                    placeholder="e.g., 22K Gold, Sterling Silver"
                  />
                </div>
                <div>
                  <label className="block font-body text-sm text-text-secondary mb-2">
                    Closure Type *
                  </label>
                  <input
                    type="text"
                    value={formData.closure_type}
                    onChange={(e) => setFormData({ ...formData, closure_type: e.target.value })}
                    required
                    data-testid="item-closure-type"
                    className="w-full px-4 py-3 border border-border-default focus:border-primary focus:outline-none font-body"
                    placeholder="e.g., S-Hook, Lobster Claw, Push Button"
                  />
                </div>
              </div>

              <div>
                <label className="block font-body text-sm text-text-secondary mb-2">
                  Styling Tip
                </label>
                <textarea
                  value={formData.styling_tip}
                  onChange={(e) => setFormData({ ...formData, styling_tip: e.target.value })}
                  rows={2}
                  data-testid="item-styling-tip"
                  className="w-full px-4 py-3 border border-border-default focus:border-primary focus:outline-none font-body resize-none"
                  placeholder="Style tips for pairing this jewelry..."
                />
              </div>

              <div>
                <label className="block font-body text-sm text-text-secondary mb-2">
                  Highlights
                </label>
                <textarea
                  value={formData.highlights}
                  onChange={(e) => setFormData({ ...formData, highlights: e.target.value })}
                  rows={3}
                  data-testid="item-highlights"
                  className="w-full px-4 py-3 border border-border-default focus:border-primary focus:outline-none font-body resize-none"
                  placeholder="Enter highlights, one per line (e.g., Handcrafted, Anti-tarnish, Lightweight)"
                />
                <p className="font-body text-xs text-text-muted mt-1">
                  Separate each highlight with a new line
                </p>
              </div>

              {!editingItem && (
                <div>
                  <label className="block font-body text-sm text-text-secondary mb-2">
                    Images
                  </label>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleImageChange}
                    data-testid="item-images"
                    className="w-full px-4 py-3 border border-border-default focus:border-primary focus:outline-none font-body"
                  />
                  {images.length > 0 && (
                    <p className="font-body text-xs text-text-muted mt-1">
                      {images.length} file(s) selected
                    </p>
                  )}
                </div>
              )}
              
              <div className="flex gap-4 pt-4">
                <button 
                  type="button" 
                  onClick={() => { setShowEditModal(false); resetForm(); }} 
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={submitting}
                  data-testid="save-item-btn" 
                  className="btn-primary flex-1 disabled:opacity-50"
                >
                  {submitting ? 'Saving...' : (editingItem ? 'Update Item' : 'Create Item')}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Bulk Import Modal */}
      <BulkImportModal
        isOpen={showBulkImport}
        onClose={() => setShowBulkImport(false)}
        onSuccess={fetchItems}
      />
    </div>
  );
};

export default AdminItems;
